/**
 * Shared helpers for maneuver time series: API option ordering, description strings,
 * options fetch, and parsing timeseries JSON into chart-ready rows.
 */

import { getData, round } from "./global";
import { apiEndpoints } from "../config/env";
import { debug, error as logError, warn as logWarning } from "./console";
import { assignYearEventMetadataOnPoint } from "./maneuverMetadataFields";

export interface ManeuverTsPoint {
  event_id: number;
  tws_bin: number;
  tack: string;
  vmg_perc_avg: number;
  race: number | string;
  source_name: string;
  State?: string;
  Config?: string;
  time: number;
  [key: string]: unknown;
}

/** Payload channels that stay on each point (color, table) and are omitted as their own chart. */
const UNDRAWN_MANEUVER_TIMESERIES_CHANNELS = new Set(["vmg_perc", "vmg_percent"]);

/** True when this channel should be drawn as a maneuver timeseries panel. VMG percent is omitted. */
export function isManeuverTimeseriesChartChannel(name: string): boolean {
  const key = String(name ?? "").trim().toLowerCase();
  return key.length > 0 && !UNDRAWN_MANEUVER_TIMESERIES_CHANNELS.has(key);
}

/** Put BASICS first when present; preserve first-seen order; drop duplicate keys (bad API/config lists caused duplicate section headers). */
export function orderWithBasicsFirst(options: string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const o of options) {
    const u = String(o ?? "")
      .trim()
      .toUpperCase();
    if (!u || seen.has(u)) continue;
    seen.add(u);
    unique.push(u);
  }
  if (unique.length === 0) return ["BASICS"];
  const rest = unique.filter((k) => k !== "BASICS");
  if (unique.includes("BASICS")) {
    return ["BASICS", ...rest];
  }
  return rest;
}

/**
 * Build API `description` query param from global phase and uppercase option (e.g. BASICS → 1_Basics).
 */
export function buildTimeseriesDescription(phaseVal: string, optionUpper: string): string {
  let phaseNumber = "0";
  if (phaseVal === "INVESTMENT") {
    phaseNumber = "1";
  } else if (phaseVal === "TURN") {
    phaseNumber = "2";
  } else if (phaseVal === "ACCELERATION") {
    phaseNumber = "3";
  }
  const u = String(optionUpper || "BASICS").toUpperCase();
  const formatted = u.charAt(0).toUpperCase() + u.slice(1).toLowerCase();
  return `${phaseNumber}_${formatted}`;
}

export async function fetchManeuverTimeseriesOptions(
  className: string,
  projectId: string,
  signal?: AbortSignal
): Promise<string[]> {
  try {
    const path = `${apiEndpoints.app.classes}/object?class_name=${encodeURIComponent(
      className
    )}&project_id=${encodeURIComponent(projectId)}&object_name=maneuver_timeseries_options`;
    const result = await getData(path, signal);
    if (result?.success && result?.data?.data && Array.isArray(result.data.data)) {
      const ordered = orderWithBasicsFirst(result.data.data.map((opt: string) => String(opt).toUpperCase()));
      debug("fetchManeuverTimeseriesOptions: loaded", ordered);
      return ordered;
    }
    logWarning("fetchManeuverTimeseriesOptions: invalid or empty response");
    return ["BASICS"];
  } catch (e: unknown) {
    const name = e && typeof e === "object" && "name" in e ? String((e as { name?: string }).name) : "";
    if (name === "AbortError") {
      return ["BASICS"];
    }
    logError("fetchManeuverTimeseriesOptions:", e);
    return ["BASICS"];
  }
}

export interface ParseTimeSeriesResponseInput {
  timeseriesResponse: unknown;
  eventIds: number[];
  isEventHidden: (eventId: number) => boolean;
  tableRows: Array<Record<string, unknown>>;
  phaseVal: string;
}

export interface ParseTimeSeriesResponseResult {
  dataArray: ManeuverTsPoint[][];
  charts: string[];
  channelOrder: string[];
}

export interface ManeuverTimeseriesValuesEvent {
  event_id: number;
  values: Array<Record<string, unknown>>;
}

export interface ParseManeuverTimeseriesValuesResult {
  dataArray: ManeuverTsPoint[][];
  charts: string[];
  channelOrder: string[];
}

/** Stable fetch key: context|eventType|phase|sortedEventIds */
export function buildManeuverTimeseriesFetchKey(input: {
  context: string;
  eventType: string;
  phase: string;
  eventIds: number[];
}): string {
  const ids = [...new Set(input.eventIds.filter((id) => Number.isFinite(id)))].sort((a, b) => a - b).join(",");
  return `${input.context}|${input.eventType}|${input.phase}|${ids}`;
}

const FOIL_OLD_NEW_CHANNEL_ALIASES = [
  ["rud_imm_old_mm", "rud_imm_lwd_mm"],
  ["rud_imm_new_mm", "rud_imm_wwd_mm"],
  ["rh_old_mm", "rh_lwd_mm"],
  ["rh_new_mm", "rh_wwd_mm"],
  ["db_ext_old_mm", "db_ext_lwd_mm"],
  ["db_ext_new_mm", "db_ext_wwd_mm"],
  ["db_stow_old", "db_stow_state_lwd"],
  ["db_stow_new", "db_stow_state_wwd"],
  ["db_cant_old_deg", "db_cant_lwd_deg"],
  ["db_cant_new_deg", "db_cant_wwd_deg"],
  ["db_rake_old_deg", "db_rake_lwd_deg"],
  ["db_rake_new_deg", "db_rake_wwd_deg"],
  ["db_rake_aoa_old_deg", "db_rake_aoa_lwd_deg"],
  ["db_rake_aoa_new_deg", "db_rake_aoa_wwd_deg"],
] as const;

function addFoilOldNewAliasOrder(channelOrder: string[]): string[] {
  const result = [...channelOrder];
  const seen = new Set(result.map((channel) => channel.toLowerCase()));

  for (const [oldNewChannel, lwdWwdChannel] of FOIL_OLD_NEW_CHANNEL_ALIASES) {
    if (seen.has(lwdWwdChannel) && !seen.has(oldNewChannel)) {
      result.push(oldNewChannel);
      seen.add(oldNewChannel);
    }
  }

  return result;
}

function copyFoilOldNewAliases(point: ManeuverTsPoint): void {
  for (const [oldNewChannel, lwdWwdChannel] of FOIL_OLD_NEW_CHANNEL_ALIASES) {
    if (point[oldNewChannel] === undefined && point[lwdWwdChannel] !== undefined) {
      point[oldNewChannel] = point[lwdWwdChannel];
    }
  }
}

/** Map slim /maneuvers-timeseries-values rows into chart-ready series (lowercased channel keys). */
export function parseManeuverTimeseriesValuesResponse(
  rows: ManeuverTimeseriesValuesEvent[],
  eventIds: number[],
  channelOrder: string[]
): ParseManeuverTimeseriesValuesResult {
  const charts: string[] = [];
  const order = addFoilOldNewAliasOrder(channelOrder.map((c) => c.toLowerCase()));
  const byEvent = new Map<number, ManeuverTsPoint[]>();

  for (const row of rows) {
    const eventId = Number(row.event_id);
    if (!Number.isFinite(eventId)) continue;
    const points: ManeuverTsPoint[] = [];
    for (const valueRow of row.values ?? []) {
      const t = Number(valueRow.time);
      if (!Number.isFinite(t)) continue;
      const point: ManeuverTsPoint = {
        event_id: eventId,
        tws_bin: 0,
        tack: "",
        vmg_perc_avg: 0,
        race: "",
        source_name: "",
        time: t,
      };
      for (const ch of order) {
        const raw = valueRow[ch] ?? valueRow[ch.toLowerCase()] ?? valueRow[ch.toUpperCase()];
        if (raw !== undefined && raw !== null) {
          point[ch] = round(Number(raw), 2);
        }
      }
      copyFoilOldNewAliases(point);
      points.push(point);
    }
    byEvent.set(eventId, points);
  }

  const dataArray: ManeuverTsPoint[][] = eventIds
    .map((id) => byEvent.get(id))
    .filter((series): series is ManeuverTsPoint[] => !!series && series.length > 0);

  return { dataArray, charts, channelOrder: order };
}

export function normalizeManeuverPhaseParam(phase: string | undefined): string {
  const p = String(phase ?? "FULL").trim().toUpperCase();
  if (p === "INVESTMENT" || p === "TURN" || p === "ACCELERATION") return p;
  return "FULL";
}

/**
 * Normalize fetch response and join table metadata — same pipeline as legacy TimeSeries fetchData.
 */
export function parseTimeSeriesResponse(input: ParseTimeSeriesResponseInput): ParseTimeSeriesResponseResult {
  const { timeseriesResponse, eventIds, isEventHidden, tableRows, phaseVal } = input;

  let json_data: unknown[] = [];
  let charts: string[] = [];

  if (Array.isArray(timeseriesResponse)) {
    json_data = timeseriesResponse;
    if (json_data.length > 0 && json_data[0] && typeof json_data[0] === "object" && "json" in json_data[0]) {
      try {
        const raw = (json_data[0] as { json: unknown }).json;
        const firstJson = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (firstJson && typeof firstJson === "object" && "charts" in firstJson && Array.isArray((firstJson as { charts: unknown }).charts)) {
          charts = (firstJson as { charts: string[] }).charts;
        }
      } catch {
        /* ignore */
      }
    }
  } else if (timeseriesResponse && typeof timeseriesResponse === "object" && "data" in timeseriesResponse) {
    const responseObj = timeseriesResponse as { data: unknown[]; charts?: string[] };
    json_data = responseObj.data || [];
    if (responseObj.charts && Array.isArray(responseObj.charts)) {
      charts = responseObj.charts;
    } else if (json_data.length > 0 && json_data[0] && typeof json_data[0] === "object" && "json" in json_data[0]) {
      try {
        const raw = (json_data[0] as { json: unknown }).json;
        const firstJson = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (firstJson && typeof firstJson === "object" && "charts" in firstJson && Array.isArray((firstJson as { charts: unknown }).charts)) {
          charts = (firstJson as { charts: string[] }).charts;
        }
      } catch {
        /* ignore */
      }
    }
  }

  if (!json_data || json_data.length === 0) {
    return { dataArray: [], charts: [], channelOrder: [] };
  }

  const tableDataMap = new Map<number, Record<string, unknown>>();
  tableRows.forEach((row) => {
    const eid = row.event_id;
    if (typeof eid === "number") {
      tableDataMap.set(eid, row);
    }
  });

  const getTackValue = (twa_entry: number): string => {
    if (phaseVal === "FULL" || phaseVal === "TURN") {
      return twa_entry > 0 ? "S - P" : "P - S";
    }
    if (phaseVal === "INVESTMENT") {
      return twa_entry > 0 ? "STBD" : "PORT";
    }
    return twa_entry > 0 ? "PORT" : "STBD";
  };

  const dataArray: ManeuverTsPoint[][] = [];
  const eventIdSet = new Set(eventIds);
  const filtered_data = json_data.filter(
    (d: unknown) =>
      d &&
      typeof d === "object" &&
      "event_id" in d &&
      eventIdSet.has((d as { event_id: number }).event_id) &&
      !isEventHidden((d as { event_id: number }).event_id)
  );

  let channelOrder: string[] = [];
  const first = filtered_data[0] as { json?: { values?: Record<string, unknown>[] } } | undefined;
  if (first?.json?.values && first.json.values.length > 0) {
    const firstValue = first.json.values[0];
    channelOrder = addFoilOldNewAliasOrder(Object.keys(firstValue).filter((key) => key !== "time" && key !== "event_id"));
  }

  for (let i = 0; i < filtered_data.length; i++) {
    const item = filtered_data[i] as {
      event_id: number;
      tws_bin?: number;
      vmg_perc_avg?: number;
      twa_entry?: number;
      Race_number?: unknown;
      source_name?: string;
      state?: string;
      State?: string;
      config?: string;
      Config?: string;
      json: { values: Array<Record<string, unknown>> };
    };
    const tableRow = tableDataMap.get(item.event_id);

    const eventId = item.event_id;
    const twsBin = (tableRow?.tws_bin as number | undefined) ?? item.tws_bin;
    const vmgPercAvg = (tableRow?.vmg_perc_avg as number | undefined) ?? item.vmg_perc_avg;
    const twaEntry = (tableRow?.twa_entry as number | undefined) ?? item.twa_entry;
    const raceValue =
      (tableRow?.Race_number as unknown) ??
      (tableRow?.race_number as unknown) ??
      (tableRow?.race as unknown) ??
      item.Race_number;
    const race = raceValue === -1 || raceValue === "-1" ? "TRAINING" : raceValue;
    const sourceName = (tableRow?.source_name as string | undefined) ?? item.source_name ?? "";
    const state = (tableRow?.state as string | undefined) ?? (tableRow?.State as string | undefined) ?? item.state ?? item.State;
    const config = (tableRow?.config as string | undefined) ?? (tableRow?.Config as string | undefined) ?? item.config ?? item.Config;

    const tackValue = getTackValue(Number(twaEntry) || 0);
    const values = item.json.values;
    const processedData: ManeuverTsPoint[] = new Array(values.length);

    for (let j = 0; j < values.length; j++) {
      const d = values[j];
      const dataPoint: ManeuverTsPoint = {
        event_id: eventId,
        tws_bin: twsBin as number,
        tack: tackValue,
        vmg_perc_avg: vmgPercAvg as number,
        race: race as number | string,
        source_name: sourceName,
        time: parseFloat(String(d.time)),
      };
      if (state !== undefined && state !== null) {
        dataPoint.State = state;
      }
      if (config !== undefined && config !== null) {
        dataPoint.Config = config;
      }
      assignYearEventMetadataOnPoint(dataPoint as unknown as Record<string, unknown>, item as Record<string, unknown>, tableRow);
      for (const channel of channelOrder) {
        if (d[channel] !== undefined && channel !== "time" && channel !== "event_id") {
          dataPoint[channel] = round(d[channel] as number, 2);
        }
      }
      copyFoilOldNewAliases(dataPoint);
      processedData[j] = dataPoint;
    }
    dataArray.push(processedData);
  }

  return { dataArray, charts, channelOrder };
}
